/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/SelectedAPI',
	'../../../../util/EventUtils',
	'underscore'
], function(APIFactory, SelectedAPI, EventUtils, _) {

	/**
	 * VisSelected provides a fast lookup on the selected values for a particular item or tuple.
	 */
	class VisSelected {

		/**
		 * constructor attributes:
		 * pageContextAPI - the pageContextAPI
		 * itemIds - an array of itemIds
		 */
		constructor(attributes) {
			this._includeParents = attributes.includeParents;
			this._ignoreEdgeSelections = attributes.ignoreEdgeSelections;
			this._edgeSelections = [];
			this._pointSelections = {};
			this._selectedParents = {};

			this._pageContextAPI = attributes.pageContextAPI;
			this._itemIds = attributes.itemIds;
			this._sourceId = attributes.sourceId;
			this.synchronizeData = attributes.synchronizeData;

			const pageContextMatchParams = {
				origin: 'visualization',
				scope: attributes.scope,
				eventGroupId: attributes.eventGroupId
			};

			if(!this._ignoreEdgeSelections){
				_.each(this._itemIds, itemId => {
					const pci = this.synchronizeData.getPageContextItem(this._sourceId, this._pageContextAPI, itemId, pageContextMatchParams);
					if (pci && pci.getValueCount()) {
						let selectedValueMap = {};
						let aTupleValues = [];
						_.each(pci.getValueIds(), valueId => {
							selectedValueMap[valueId] = 1;
							aTupleValues.push(valueId);
						});
						this._edgeSelections.push(selectedValueMap);
					} else {
						this._edgeSelections.push({});
					}
				});
			}

			//Lookup pageContext entries for the items of interest and place the selected datapoints (if any) in a map.
			const hierarchies = _.map(this._itemIds, itemId => {
				return {
					hierarchyUniqueName: itemId
				};
			});

			for (let i = this._itemIds.length; i > 1; --i) {
				this._addDataPointSelections(_.head(hierarchies, i), pageContextMatchParams, attributes.includeParents);
			}

			//Cache 'parent selected points' to detect is something is a 'selectedChild point' (selectedChild points of edges are handled above)
			//A selected child point/cell is not really selected itself but are highlighted if there are selections of 'parent points'
			//When asking if point A.B.C is selected, we want to know if A.B is selected so that we can return it as a 'selectedChild'.
			//When asking if A.B.C.D is selected we want to know if A.B, A.B.C or B.C are selected so that we can return it as a 'selectedChild'
			for (let start = 0; start < this._itemIds.length - 1; ++start) {
				for (let end = this._itemIds.length; end > start + 1; --end) {
					//Set includeParents to false for selected children...we don't want to process parents of selected children again.
					this._addDataPointSelections(hierarchies.slice(start, end), pageContextMatchParams, /*includeParents*/ false);
				}
			}
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [SelectedAPI]);
			}
			return this._api;
		}

		_addDataPointSelections(hierarchies, pageContextMatchParams, includeParents) {
			const pcpoint = this._pageContextAPI.getPageContextItem(_.extend({
				hierarchies: hierarchies
			}, pageContextMatchParams));
			if (pcpoint && pcpoint.getValueCount()) {
				_.each(pcpoint.getDataPoints(), function(dataPoint) {
					this._pointSelections[this._getDataPointKey(dataPoint)] = 1;
					if (includeParents) {
						this._addSelectedParents(this._itemIds, dataPoint);
					}
				}.bind(this));
			}
		}

		/**
		 * Given Year, Product, OrderMethod, gender
		 *       2007,  cars,     fax,		  male
		 * Year=2007
		 * Year=2007__Product=Cars
		 * Year=2007__Product=Cars__OrderMethod=Fax
		 * Year=2007__Product=Cars__OrderMethod=Fax__Gender=male
		 * Product=cars
		 * Product=cars__OrderMethod=fax
		 * Product=cars__OrderMethod=fax__gender=male
		 * OrderMethod=fax__gender=male
		 * gender=male
		 *
		 * ASSUMPTION - all items from the beginning of an edge are provided
		 * @return true for any in-order permutation
		 */
		_addSelectedParents(itemIds, dataPoint) {
			//Generate a tuple for each of length 1 to length n-1 where n-1 is the length of the array.
			let itemList = itemIds;
			let tuple = dataPoint;
			while (itemList.length > 0) {
				for (let tupleLength = 1; tupleLength < itemList.length; ++tupleLength) {

					this._selectedParents[this._getParentKey(_.first(itemList, itemList.length), _.first(tuple, tupleLength))] = 1;
				}
				itemList = _.rest(itemList);
				tuple = _.rest(tuple);
			}
		}

		/**
		 * length of the longest selected point
		 * AxBxC returns 3
		 * AxB returns 2
		 */
		_getMaxPointSelectionLength() {
			let max = 0;
			const selections = Object.getOwnPropertyNames(this._pointSelections);
			for (let i = 0; i < selections.length; i++){
				max = Math.max(selections[i].split(EventUtils.SEPARATOR).length, max);
			}
			return max;
		}

		/**
		 * @param tuple - a tuple of form [ { u:'x'}, { u:'y'} ] where each entry in the array corresponds to the itemIds array.
		 *				  (also supports form [ 'x', 'y'] ) where x and y are assumed to be use values.
		 * example: isSelected [ {u:'2005'}, {u:'Fax'} ]
		 */
		isSelected(tuple) {
			//First process the edges....For edges, if at least 1 is selected, the node is selected.
			for (let i = 0; i < tuple.length; ++i) {
				if (tuple[i] && _.isObject(this._edgeSelections[i]) && this._edgeSelections[i][tuple[i].u || tuple[i]]) {
					//Any match of an edge is a match.
					return true;
				}
			}

			//Process dataPoints....it has to be an exact match.
			if (this._pointSelections[this._getDataPointKey(tuple)]) {
				return true;
			}

			//if 'A' is a selected value, A.B is a selectedChild, A.B.C is a selectedChild etc.
			//if 'A.B' is a selected datapoint, A.B.C is a selectedChild (A is a parent)
			//if 'A.B.C' is a selected datapoint, A.B.C.D is a selectedChild but so is B.C.D (A.B, A are parents)
			const maxLength = this._getMaxPointSelectionLength();
			for (let start = 0; start < tuple.length; ++start) {
				for (let end = tuple.length; end >= start; --end) {
					let slice = tuple.slice(start, end);
					if (slice.length <= maxLength && this._pointSelections[this._getDataPointKey(slice)]) {
						return true;
					}
				}
			}

			return false;
		}

		isSelectedParent(hierarchies, dataPoint) {
			return this._selectedParents[this._getParentKey(hierarchies, dataPoint)] ? true : false;
		}

		/**
		 * Same as isSelected but for a simple value object.
		 * @param value - a value of form { u: 'x' }
		 */
		isValueSelected(value) {
			return this.isSelected([value]);
		}

		hasSelections() {
			const hasEdgeSelections = _.find(this._edgeSelections, edge => {
				return (_.keys(edge).length > 0);
			});
			return (hasEdgeSelections || _.keys(this._pointSelections).length) ? true : false;
		}

		_getDataPointKey(dataPoint){
			return EventUtils.getDataPointKey(dataPoint);
		}

		_getParentKey(hierarchies, dataPoint) {
			let key = '';
			const tempDataPoint = _.clone(dataPoint); //Make a copy to sort and keep dataPoint unchanged.
			const sortedDataPoint = tempDataPoint.sort(EventUtils.compareDataPointKey);
			_.each(sortedDataPoint, (tuplePart, partIdx) => {
				key += (hierarchies[partIdx] + '=' + tuplePart.u + EventUtils.SEPARATOR);
			});
			return key.slice(0, -1);
		}

		getSelectedKeys() {
			let selectedKeys = [];

			//Get edge selection keys
			_.each(this._edgeSelections,function(edgeSelection){
				selectedKeys = selectedKeys.concat(_.keys(edgeSelection));
			});

			//Get datapoint keys
			selectedKeys = selectedKeys.concat(_.keys(this._pointSelections));

			return selectedKeys;
		}
	}

	return VisSelected;
});
